import { type PropsWithChildren, useLayoutEffect, useState } from "react";
import { type IntlConfig, IntlProvider } from "react-intl";
import { loadLocaleData } from "./polyfill";
import { useLanguageCode } from "./utils/hooks/useLanguageCode";
import {
  supportedLanguageCodes,
  type SupportedLanguageCode,
} from "./utils/language";

const DEFAULT_LANG = "en";
const DEFAULT_LOCALE = "en-gb";
const DEFAULT_MESSAGES = {};

export function L10nProvider(
  props: PropsWithChildren<{
    onError?: IntlConfig["onError"];
  }>,
) {
  const languageCode = useLanguageCode();

  // Note: The locale is basically just the language for now, we might want to consider allowing culture-specific locales, e.g. en-us
  const locale = languageCode === DEFAULT_LANG ? DEFAULT_LOCALE : languageCode;

  // Load the locale & messages data and trigger this component and all our children to
  // re-render once they are loaded.
  const [messages, setMessages] = useState<IntlConfig["messages"]>();
  const [loadedLocale, setLoadedLocale] = useState<string>();

  useLayoutEffect(() => {
    // Import the messages
    if (languageCode in languageImportMap) {
      let cancel = false;
      Promise.all([
        languageImportMap[languageCode](),
        loadLocaleData(languageCode),
      ])
        .then(([messages]) => {
          if (cancel) {
            return;
          }
          setMessages(messages);
          setLoadedLocale(locale);
        })
        .catch(() => {
          setMessages(DEFAULT_MESSAGES);
          setLoadedLocale(DEFAULT_LOCALE);
        });
      return () => {
        cancel = true;
      };
    } else {
      // Fallback to english if we can't get the users language
      setMessages(DEFAULT_MESSAGES);
      setLoadedLocale(DEFAULT_LOCALE);
    }
  }, [languageCode, locale]);

  // Only render the IntlProvider once we have the locale and messages
  if (loadedLocale === undefined || messages === undefined) {
    return null;
  }

  return (
    <IntlProvider
      locale={loadedLocale}
      messages={messages}
      defaultLocale="en-gb"
      onError={props.onError}
    >
      {props.children}
    </IntlProvider>
  );
}

type LanguageMap = Record<string, () => Promise<IntlConfig["messages"]>>;
const languageImportMap: LanguageMap = buildLanguageImportMap();

function importLocaleData(
  languageCode: SupportedLanguageCode,
): Promise<IntlConfig["messages"]> {
  return import(`./translations/translations-${languageCode}.json`).then(
    (moduleImport) => moduleImport.default,
  );
}

function buildLanguageImportMap(): LanguageMap {
  const languageImportMap: LanguageMap = {};

  for (const languageCode of supportedLanguageCodes) {
    languageImportMap[languageCode] = () =>
      importLocaleData(languageCode as SupportedLanguageCode);
  }

  // English is the default, so we don't need to import anything
  languageImportMap.en = () => Promise.resolve({});

  return languageImportMap;
}
